import { useEffect, useState } from "react";
import { Switch } from "@chakra-ui/react";

import SelectColumnDropdown from "@/components/Enrichment/Common/SelectColumnDropdown";
import SaveAndRunEnrichment from "@/components/Enrichment/Common/SaveAndRun";
import { getInitialColumn, preselectEnrichmentState } from "@/utils";
import { useTableStore } from "@/stores/table.store";
import { useEnrichStore } from "@/stores/enrich.store";
// import SearchInputAccordion from "@/components/Common/SearchInputAccordion";
import ConditionalFormula from "../../Common/ConditionalFormula";

import type { TSelectedColumnOption } from "@/types/common.types";
import { RESPONSE_COLUMNS } from "./constants";
import { REGEX } from "../../constants";
import { EnrichmentMetaData } from "@/types/table.types";
import MultiSelectDropdown from "@/components/Common/MultiSelectDropdown";
import { Option } from "@/components/Common/MultiSelectDropdown/types";

interface EnrichmentDataType extends EnrichmentMetaData {
  extraColumns?: string[];
  excludedJobTitles?: Option[];
  includedJobTitles?: Option[];
  isPrioritizeJobTitle?: boolean;
  companyNameId?: string;
}

const FindPeopleAtCompany = () => {
  const [selectedOption, setSelectedOption] =
    useState<TSelectedColumnOption | null>(null);
  const [isPrioritizeJobTitle, setIsPrioritizeJobTitle] = useState(false);
  const [includedJobTitles, setIncludedJobTitles] = useState<Option[] | []>([]);
  const [excludedJobTitles, setExcludedJobTitles] = useState<Option[] | []>([]);
  const [pastCompanies, setPastCompanies] = useState<Option[] | []>([]);
  const [responseColumns, setResponseColumns] = useState(
    () => RESPONSE_COLUMNS,
  );

  const columns = useTableStore((state) => state.tableData.columns);
  const viewMode = useEnrichStore((state) => state.viewMode);
  const selectedColumnToEdit = useEnrichStore(
    (state) => state.selectedColumnToEdit,
  );
  const updateState = useEnrichStore((state) => state.updateState);

  const handleSwitch = (id: string) => {
    setResponseColumns((prevState) =>
      prevState.map((item) =>
        item.id === id ? { ...item, isChecked: !item.isChecked } : item,
      ),
    );
  };

  const updateJobTitlesState = (titles: string | string[], setState: any) => {
    if (Array.isArray(titles)) {
      setState(
        titles.map((item) => ({
          label: item,
          value: item,
        })),
      );
    } else {
      const jobTitls = titles.split(",");
      setState(
        jobTitls.map((item) => ({
          label: item,
          value: item,
        })),
      );
    }
  };

  useEffect(() => {
    if (viewMode === "edit") {
      const enrichmentData = selectedColumnToEdit?.metaData
        ?.enrichmentMetaData as EnrichmentDataType;
      if (enrichmentData) {
        preselectEnrichmentState(enrichmentData, setSelectedOption);
        updateState({
          runConditionFormula: enrichmentData.runCondition,
          isInvalidConditionFormula: false,
        });

        if (enrichmentData.extraColumns) {
          setResponseColumns((prevState) =>
            prevState.map((item) => ({
              ...item,
              isChecked:
                enrichmentData?.extraColumns?.includes(item.label) ?? false,
            })),
          );
        }

        updateJobTitlesState(
          // @ts-ignore
          enrichmentData.includedJobTitles || [],
          setIncludedJobTitles,
        );
        updateJobTitlesState(
          // @ts-ignore
          enrichmentData.pastCompanies || [],
          setPastCompanies,
        );
        updateJobTitlesState(
          // @ts-ignore
          enrichmentData.excludedJobTitles || [],
          setExcludedJobTitles,
        );
      }

      return;
    }

    const initialColumn = getInitialColumn(REGEX.WEBSITE);
    if (initialColumn) {
      setSelectedOption({
        key: initialColumn.name,
        keyId: initialColumn._id,
        iconType: initialColumn.metaData?.iconType || "url",
      });
    }
  }, [columns, viewMode]);

  return (
    <>
      <div className="flex-grow  p-4 overflow-y-auto max-h-[70vh]">
        <p className="pb-1 text-base font-semibold">Company Identifier</p>
        <p className="pb-1.5 text-xs">
          It can be a company website url or company name or company linkedin url or company ID. Company ID provides better
          results, so use our Company Enrichment first to get the id
        </p>
        <SelectColumnDropdown
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
        />

        <div className="pt-3 space-y-4">
          <div className="pt-4">
            <p className="text-base font-semibold pb-1">
              Past Companies to include
            </p>
            <p className="pb-1.5 text-xs">
              Company id can be found using our enrich company enrichment.
            </p>
            <MultiSelectDropdown
              value={pastCompanies}
              options={[]}
              onChange={(selectedValues) => {
                setPastCompanies(selectedValues as Option[]);
              }}
              placeholder="Add past companies id to include"
              limit={20}
            />
          </div>
          <div className="">
            <p className="text-base font-semibold">Job Title</p>
            <span className="block pb-1">
              You can add upto three job titles.
            </span>

            <div className="mb-2 mt-2 d-flex hidden">
              <input
                type="checkbox"
                checked={isPrioritizeJobTitle}
                onChange={(e) => setIsPrioritizeJobTitle(e.target.checked)}
                id="prioritize-job-title"
              />
              <label htmlFor="prioritize-job-title" className="ml-2">
                Prioritze Leads based on Job title
              </label>
            </div>
            <MultiSelectDropdown
              value={includedJobTitles}
              options={[]}
              onChange={(selectedValues) => {
                setIncludedJobTitles(selectedValues as Option[]);
              }}
              placeholder="Add job titles to include"
              limit={3}
            />
          </div>
          <div className="">
            <p className="mb-2 text-base">
              Job Title Exclude Keywords -{" "}
              <span className="text-gray-600 text-xs">Optional</span>
            </p>
            <MultiSelectDropdown
              value={excludedJobTitles}
              options={[]}
              onChange={(selectedValues) => {
                setExcludedJobTitles(selectedValues as Option[]);
              }}
              placeholder="Add job titles to exclude"
              limit={3}
            />
          </div>

          {/* <div className="">
            <p className="text-base font-semibold">
              Keyword - <span className="text-gray-600 text-xs">Optional</span>
            </p>
            <Input
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
              }}
              placeholder="Add keyword to include"
            />
          </div> */}

          {/* <SearchInputAccordion
            btnStyle={{
              backgroundColor: "rgb(250 250 251)",
              borderRadius: "6px",
            }}
            button={
              <span className="w-full truncate text-left font-semibold text-[#000000]">
                Use AI to improve results
              </span>
            }
          >
            <div className="pt-1 pb-2">
              <p className="text-base font-semibold mb-2">Company Name</p>
              <SelectColumnDropdown
                selectedOption={selectedCompanyName}
                setSelectedOption={setSelectedCompanyName}
              />
            </div>
          </SearchInputAccordion> */}

          <div className="flex flex-col gap-[8px]">
            <div className="flex items-center justify-between mb-2">
              <p className="text-base font-semibold">
                Add data as columns to your table
              </p>
              <p className="text-grey-400 text-[14px] text-[#676d7e]">
                {responseColumns.filter((item) => item.isChecked).length}{" "}
                columns selected
              </p>
            </div>
            <div className="space-y-4">
              {responseColumns.map(({ id, label, isChecked }) => (
                <div
                  key={id}
                  className="flex w-full items-center justify-between rounded-[6px] bg-[#FAFAFB] px-4 py-2"
                >
                  <p className="text-[16px] font-[500]">{label}</p>
                  <Switch
                    size={"md"}
                    isChecked={isChecked}
                    id={id}
                    onChange={() => handleSwitch(id)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <ConditionalFormula />
      </div>

      <SaveAndRunEnrichment
        isDisabled={!selectedOption?.key}
        apiPayload={{
          columnId: selectedOption?.keyId,
          excludedJobTitles: excludedJobTitles?.map((item) => item.value),
          includedJobTitles: includedJobTitles?.map((item) => item.value),
          pastCompanies: pastCompanies?.map((item) => item.value),
          isPrioritizeJobTitle: false,
          newColumnNames: responseColumns
            .filter((item) => item.isChecked)
            .map((item) => item.label),
        }}
      />
    </>
  );
};

export default FindPeopleAtCompany;
